import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { Supplier, PurchaseOrderSummary, PurchaseOrderDetail, StockItem } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { PoPrintModal } from '@/components/store/PoPrintModal'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Plus,
  Package,
  Truck,
  Info,
  Mail,
  Phone,
  User,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Printer,
  ChevronDown,
  Trash2,
  RotateCcw,
  Pencil,
} from 'lucide-react'
import { cn, formatDateHuman } from '@/lib/utils'

type ToastFn = (type: 'success' | 'error', message: string) => void

type PoSortKey = 'supplier_name' | 'created_at' | 'expected_date' | 'total_ordered_qty' | 'status'

const PURCHASING_GUIDE_EXPANDED_KEY = 'pos_inventory_purchasing_guide_expanded'

function readPurchasingGuideExpanded(): boolean {
  try {
    const raw = localStorage.getItem(PURCHASING_GUIDE_EXPANDED_KEY)
    if (raw === null) return true
    return raw === 'true'
  } catch {
    return true
  }
}

function formatPoStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: 'Draft',
    pending_approval: 'Pending approval',
    approved: 'Approved',
    ordered: 'Ordered',
    partially_received: 'Partially received',
    received: 'Received',
    cancelled: 'Cancelled',
  }
  return labels[status] ?? status.replace(/_/g, ' ')
}

function poStatusBadgeVariant(status: string): 'secondary' | 'outline' | 'info' | 'warning' | 'success' | 'destructive' {
  switch (status) {
    case 'draft':
      return 'secondary'
    case 'pending_approval':
      return 'warning'
    case 'approved':
    case 'ordered':
      return 'info'
    case 'partially_received':
      return 'warning'
    case 'received':
      return 'success'
    case 'cancelled':
      return 'destructive'
    default:
      return 'outline'
  }
}

export function InventoryPurchasingTab({ stockItems, showToast }: { stockItems: StockItem[]; showToast: ToastFn }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [supplierModal, setSupplierModal] = useState(false)
  const [deleteSupplierTarget, setDeleteSupplierTarget] = useState<Supplier | null>(null)
  const [poModal, setPoModal] = useState(false)
  const [receivePoId, setReceivePoId] = useState<string | null>(null)
  const [poPrintOpen, setPoPrintOpen] = useState(false)
  const [poPrintDetail, setPoPrintDetail] = useState<PurchaseOrderDetail | null>(null)
  const [printingPoId, setPrintingPoId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PurchaseOrderSummary | null>(null)
  const [closeTarget, setCloseTarget] = useState<PurchaseOrderSummary | null>(null)
  const [closeReason, setCloseReason] = useState('')
  const [deletePin, setDeletePin] = useState('')
  const [deleteReason, setDeleteReason] = useState('')
  const [returnPoId, setReturnPoId] = useState<string | null>(null)
  const [returnLineId, setReturnLineId] = useState('')
  const [returnQty, setReturnQty] = useState('')
  const [returnRefundType, setReturnRefundType] = useState<'cash' | 'bank' | 'supplier_credit' | 'no_refund'>('supplier_credit')
  const [returnRefundAmount, setReturnRefundAmount] = useState('')
  const [returnReason, setReturnReason] = useState('')
  const [returnPin, setReturnPin] = useState('')
  const [purchasingGuideExpanded, setPurchasingGuideExpanded] = useState(readPurchasingGuideExpanded)

  const togglePurchasingGuide = useCallback(() => {
    setPurchasingGuideExpanded((prev) => {
      const next = !prev
      try {
        localStorage.setItem(PURCHASING_GUIDE_EXPANDED_KEY, String(next))
      } catch {
        /* ignore quota / private mode */
      }
      return next
    })
  }, [])
  const [supplierForm, setSupplierForm] = useState({ name: '', contact_name: '', phone: '', email: '', commission_percent: '', is_consignment: false })
  // When set, the supplier dialog is editing this vendor (vs creating a new one).
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [poForm, setPoForm] = useState({
    supplier_id: '',
    expected_date: '',
    notes: '',
    lines: [{ stock_item_id: '', quantity_ordered: 1 as number, unit_cost: undefined as number | undefined }],
  })

  const { data: supRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers() })
  const suppliers: Supplier[] = supRes?.data ?? []

  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
    enabled: apiClient.isAuthenticated(),
    retry: false,
  })
  const poReceiptSettings = parseReceiptSettings(
    settingsRes?.success && settingsRes.data ? (settingsRes.data as Record<string, unknown>) : undefined,
  )
  const poPrintBusinessName = poReceiptSettings.businessName

  const { data: poRes } = useQuery({
    queryKey: ['purchaseOrders'],
    queryFn: () => apiClient.listPurchaseOrders({ page: 1, per_page: 50 }),
  })
  const orders: PurchaseOrderSummary[] = poRes?.data ?? []

  const [poSort, setPoSort] = useState<{ key: PoSortKey; dir: 'asc' | 'desc' }>({
    key: 'created_at',
    dir: 'desc',
  })

  const onPoSort = useCallback((key: PoSortKey) => {
    setPoSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    )
  }, [])

  const sortedOrders = useMemo(() => {
    const { key, dir } = poSort
    const mul = dir === 'asc' ? 1 : -1
    return [...orders].sort((a, b) => {
      let c = 0
      switch (key) {
        case 'supplier_name':
          c = a.supplier_name.localeCompare(b.supplier_name, undefined, { sensitivity: 'base' })
          break
        case 'created_at':
          c = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
        case 'expected_date':
          c = (a.expected_date || '').localeCompare(b.expected_date || '')
          break
        case 'total_ordered_qty':
          c = a.total_ordered_qty - b.total_ordered_qty
          break
        case 'status':
          c = a.status.localeCompare(b.status)
          break
        default:
          break
      }
      return c * mul
    })
  }, [orders, poSort])

  const { data: detailRes } = useQuery({
    queryKey: ['purchaseOrder', receivePoId],
    queryFn: () => apiClient.getPurchaseOrder(receivePoId!),
    enabled: !!receivePoId,
  })
  const detail: PurchaseOrderDetail | undefined = detailRes?.data

  const { data: returnDetailRes } = useQuery({
    queryKey: ['purchaseOrder', returnPoId, 'return'],
    queryFn: () => apiClient.getPurchaseOrder(returnPoId!),
    enabled: !!returnPoId,
  })
  const returnDetail: PurchaseOrderDetail | undefined = returnDetailRes?.data
  const returnableLines = useMemo(
    () => (returnDetail?.lines ?? []).filter((ln) => (ln.quantity_returnable ?? ln.quantity_received) > 0.0001),
    [returnDetail],
  )

  const [receiveLines, setReceiveLines] = useState<Record<string, { qty: number; expiry: string; unit_cost: string; reason: string }>>({})
  const [receiveFinalize, setReceiveFinalize] = useState(false)
  // #3: cash-basis opt-in — book this receipt as an expense now (default off).
  const [receivePostToExpense, setReceivePostToExpense] = useState(false)

  // Commission % as a number (blank → 0); shared by create + edit.
  const commissionValue = () => {
    const n = parseFloat(supplierForm.commission_percent)
    return Number.isFinite(n) ? n : 0
  }
  const resetSupplierForm = () => setSupplierForm({ name: '', contact_name: '', phone: '', email: '', commission_percent: '', is_consignment: false })
  const openCreateSupplier = () => {
    setEditingSupplier(null)
    resetSupplierForm()
    setSupplierModal(true)
  }
  const openEditSupplier = (s: Supplier) => {
    setEditingSupplier(s)
    setSupplierForm({
      name: s.name,
      contact_name: s.contact_name ?? '',
      phone: s.phone ?? '',
      email: s.email ?? '',
      commission_percent: s.commission_percent != null ? String(s.commission_percent) : '',
      is_consignment: !!s.is_consignment,
    })
    setSupplierModal(true)
  }

  const createSup = useMutation({
    mutationFn: () =>
      apiClient.createSupplier({
        name: supplierForm.name,
        contact_name: supplierForm.contact_name || undefined,
        phone: supplierForm.phone || undefined,
        email: supplierForm.email || undefined,
        commission_percent: commissionValue(),
        is_consignment: supplierForm.is_consignment,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      showToast('success', 'Supplier created')
      setSupplierModal(false)
      resetSupplierForm()
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  const updateSup = useMutation({
    mutationFn: () => {
      if (!editingSupplier) throw new Error('No supplier selected')
      return apiClient.updateSupplier(editingSupplier.id, {
        name: supplierForm.name,
        contact_name: supplierForm.contact_name || undefined,
        phone: supplierForm.phone || undefined,
        email: supplierForm.email || undefined,
        commission_percent: commissionValue(),
        is_consignment: supplierForm.is_consignment,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      showToast('success', 'Supplier updated')
      setSupplierModal(false)
      setEditingSupplier(null)
      resetSupplierForm()
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  const deleteSupplier = useMutation({
    mutationFn: () => {
      if (!deleteSupplierTarget) throw new Error('No supplier selected')
      return apiClient.deleteSupplier(deleteSupplierTarget.id)
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['suppliers'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      showToast('success', res.message || 'Supplier deleted')
      setDeleteSupplierTarget(null)
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to delete supplier'),
  })

  const createPO = useMutation({
    mutationFn: async () => {
      const res = await apiClient.createPurchaseOrder({
        supplier_id: poForm.supplier_id,
        expected_date: poForm.expected_date || undefined,
        notes: poForm.notes || undefined,
        lines: poForm.lines
          .filter((l) => l.stock_item_id && l.quantity_ordered > 0)
          .map((l) => ({
            stock_item_id: l.stock_item_id,
            quantity_ordered: l.quantity_ordered,
            unit_cost: l.unit_cost,
          })),
      })
      if (!res.success || !res.data?.id) {
        throw new Error(res.error || res.message || 'Failed to create purchase order')
      }
      return res.data.id
    },
    onSuccess: async (newId) => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      showToast('success', 'Purchase order created')
      setPoModal(false)
      setPoForm({
        supplier_id: '',
        expected_date: '',
        notes: '',
        lines: [{ stock_item_id: '', quantity_ordered: 1, unit_cost: undefined }],
      })
      try {
        const d = await apiClient.getPurchaseOrder(newId)
        if (d.success && d.data) {
          setPoPrintDetail(d.data)
          setPoPrintOpen(true)
        }
      } catch {
        showToast('error', 'Purchase order saved, but it could not be loaded for printing.')
      }
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  const submitPO = useMutation({
    mutationFn: (id: string) => apiClient.submitPurchaseOrder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      showToast('success', 'PO submitted')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  const approvePO = useMutation({
    mutationFn: (id: string) => apiClient.approvePurchaseOrder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      showToast('success', 'Purchase order approved')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to approve'),
  })

  const closePO = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => apiClient.closePurchaseOrder(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      showToast('success', 'Purchase order closed')
      setCloseTarget(null)
      setCloseReason('')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to close'),
  })

  const sendWhatsApp = useMutation({
    mutationFn: (id: string) => apiClient.sendPurchaseOrderWhatsApp(id),
    onSuccess: (res) => {
      const url = res.data?.wa_url
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
        showToast('success', 'Opening WhatsApp…')
      } else {
        showToast('error', res.error || 'Could not build WhatsApp link')
      }
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to send'),
  })

  const openNewOrder = useCallback(() => {
    setPoForm({
      supplier_id: '',
      expected_date: '',
      notes: '',
      lines: [{ stock_item_id: '', quantity_ordered: 1, unit_cost: undefined }],
    })
    setPoModal(true)
  }, [])

  // B-P1: one stable idempotency key per open receive session. Reused on a
  // retry after a transient error so the backend dedups instead of double-
  // posting stock + a duplicate draft bill; a fresh key per PO opened.
  const receiveKey = useMemo(() => (receivePoId ? crypto.randomUUID() : ''), [receivePoId])

  const receivePO = useMutation({
    mutationFn: ({ id, lines, finalize }: { id: string; lines: { line_id: string; quantity_received: number; expiry_date?: string; unit_cost?: number; variance_reason?: string }[]; finalize?: boolean }) =>
      apiClient.receivePurchaseOrder(id, { lines, finalize, idempotency_key: receiveKey || undefined, post_to_expense: receivePostToExpense }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrder', receivePoId] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', 'Receipt recorded')
      setReceivePoId(null)
      setReceiveLines({})
      setReceiveFinalize(false)
      setReceivePostToExpense(false)
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed'),
  })

  const deletePO = useMutation({
    mutationFn: () => {
      if (!deleteTarget) throw new Error('No purchase order selected')
      return apiClient.deletePurchaseOrder(deleteTarget.id, {
        pin: deletePin.trim(),
        reason: deleteReason.trim(),
      })
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      const authorizedBy = res.data?.authorized_by
      showToast('success', authorizedBy ? `Purchase order deleted by ${authorizedBy}` : 'Purchase order deleted')
      setDeleteTarget(null)
      setDeletePin('')
      setDeleteReason('')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to delete purchase order'),
  })

  const returnPO = useMutation({
    mutationFn: () => {
      if (!returnPoId) throw new Error('No purchase order selected')
      const qty = Number(returnQty)
      const refundAmount = returnRefundType === 'no_refund' ? 0 : Number(returnRefundAmount || 0)
      return apiClient.returnPurchaseOrderLine(returnPoId, {
        purchase_order_line_id: returnLineId,
        quantity: qty,
        refund_amount: refundAmount,
        refund_type: returnRefundType,
        reason: returnReason.trim(),
        pin: returnPin.trim(),
      })
    },
    onSuccess: (res) => {
      showToast('success', res.message || 'Supplier return recorded')
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrder'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      closeReturnDialog()
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to record supplier return'),
  })

  function openReceive(id: string) {
    setReceivePoId(id)
    setReceiveLines({})
    setReceiveFinalize(false)
  }

  function openReturn(id: string) {
    setReturnPoId(id)
    setReturnLineId('')
    setReturnQty('')
    setReturnRefundType('supplier_credit')
    setReturnRefundAmount('')
    setReturnReason('')
    setReturnPin('')
  }

  function closeDeleteDialog() {
    if (deletePO.isPending) return
    setDeleteTarget(null)
    setDeletePin('')
    setDeleteReason('')
  }

  function closeReturnDialog() {
    if (returnPO.isPending) return
    setReturnPoId(null)
    setReturnLineId('')
    setReturnQty('')
    setReturnRefundType('supplier_credit')
    setReturnRefundAmount('')
    setReturnReason('')
    setReturnPin('')
  }

  function buildReceivePayload(po: PurchaseOrderDetail) {
    const lines = po.lines
      .map((ln) => {
        const r = receiveLines[ln.id]
        if (!r || r.qty <= 0) return null
        const open = ln.quantity_ordered - ln.quantity_received
        if (r.qty > open + 0.0001) return null
        const out: { line_id: string; quantity_received: number; expiry_date?: string; unit_cost?: number; variance_reason?: string } = {
          line_id: ln.id,
          quantity_received: r.qty,
        }
        if (r.expiry.trim()) out.expiry_date = r.expiry.trim()
        if (r.unit_cost.trim()) {
          const uc = parseFloat(r.unit_cost)
          if (!Number.isNaN(uc)) out.unit_cost = uc
        }
        if (r.reason && r.reason.trim()) out.variance_reason = r.reason.trim()
        return out
      })
      .filter(Boolean) as { line_id: string; quantity_received: number; expiry_date?: string; unit_cost?: number; variance_reason?: string }[]
    return lines
  }

  function poSortIcon(k: PoSortKey) {
    if (poSort.key !== k) return <ArrowUpDown className="w-4 h-4 shrink-0 opacity-40" aria-hidden />
    return poSort.dir === 'asc' ? (
      <ArrowUp className="w-4 h-4 shrink-0" aria-hidden />
    ) : (
      <ArrowDown className="w-4 h-4 shrink-0" aria-hidden />
    )
  }

  return (
    <div className="space-y-6">
      <section
        className="rounded-xl border border-primary/20 bg-primary/[0.04] p-5 sm:p-6 shadow-sm"
        aria-labelledby="purchasing-guide-title"
      >
        <div className="flex gap-4">
          <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Info className="h-6 w-6" aria-hidden />
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <button
              type="button"
              id="purchasing-guide-toggle"
              aria-expanded={purchasingGuideExpanded}
              aria-controls="purchasing-guide-panel"
              onClick={togglePurchasingGuide}
              className="flex w-full items-start justify-between gap-3 rounded-lg text-left outline-none ring-offset-background transition-colors hover:bg-background/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 -m-1 p-1 sm:-m-1.5 sm:p-1.5"
            >
              <div className="min-w-0 space-y-1">
                <h2 id="purchasing-guide-title" className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">
                  How purchasing works
                </h2>
                {!purchasingGuideExpanded && (
                  <p className="text-sm text-muted-foreground">
                    Collapsed — click to expand the walkthrough (your choice is remembered on this device).
                  </p>
                )}
              </div>
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 mt-0.5',
                  purchasingGuideExpanded && 'rotate-180',
                )}
                aria-hidden
              />
            </button>
            {purchasingGuideExpanded && (
              <div id="purchasing-guide-panel" className="space-y-3 pt-0" role="region" aria-labelledby="purchasing-guide-title">
                <p className="text-base text-muted-foreground leading-relaxed">
                  Use this tab to work with <strong className="font-medium text-foreground">vendors</strong>, raise{' '}
                  <strong className="font-medium text-foreground">purchase orders</strong>, and{' '}
                  <strong className="font-medium text-foreground">record deliveries</strong>. Receipts update stock on
                  hand (and batch costs where configured), so your inventory totals stay aligned with what you actually
                  received.
                </p>
                <ol className="grid gap-3 sm:grid-cols-2 text-base text-muted-foreground">
                  <li className="flex gap-3 rounded-lg border bg-background/80 p-3 shadow-sm">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold text-foreground">
                      1
                    </span>
                    <span>
                      <span className="font-medium text-foreground">Suppliers</span> — Add each vendor you order from
                      (contact details are optional but useful on POs and receipts).
                    </span>
                  </li>
                  <li className="flex gap-3 rounded-lg border bg-background/80 p-3 shadow-sm">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold text-foreground">
                      2
                    </span>
                    <span>
                      <span className="font-medium text-foreground">New PO</span> — Build a draft with stock lines and
                      quantities. Save as draft while you confirm; <strong className="font-medium text-foreground">Submit</strong>{' '}
                      when the order is sent to the supplier.
                    </span>
                  </li>
                  <li className="flex gap-3 rounded-lg border bg-background/80 p-3 shadow-sm">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold text-foreground">
                      3
                    </span>
                    <span>
                      <span className="font-medium text-foreground">Receive</span> — When goods arrive, open{' '}
                      <strong className="font-medium text-foreground">Receive…</strong> and enter quantities (and expiry /
                      unit cost if you track them). Partial receipts are supported.
                    </span>
                  </li>
                  <li className="flex gap-3 rounded-lg border bg-background/80 p-3 shadow-sm">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold text-foreground">
                      4
                    </span>
                    <span>
                      <span className="font-medium text-foreground">Inventory tab</span> — Use it for counts, issues, and
                      one-off purchases; use <strong className="font-medium text-foreground">Purchasing</strong> when the
                      workflow is supplier → PO → delivery.
                    </span>
                  </li>
                </ol>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(280px,360px)_1fr] lg:items-start">
        <Card className="shadow-sm">
          <CardHeader className="space-y-2 pb-4">
            <div className="flex flex-row items-start justify-between gap-3">
              <div className="space-y-1">
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <Truck className="h-5 w-5 text-primary shrink-0" /> Suppliers
                </CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Vendors you buy from. Every purchase order must reference one supplier.
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" className="h-9 shrink-0 text-sm" onClick={openCreateSupplier}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {suppliers.length === 0 ? (
              <div className="rounded-lg border border-dashed bg-muted/20 p-5 text-center">
                <p className="text-base font-medium text-foreground">No suppliers yet</p>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  Add your first supplier, then create a purchase order on the right.
                </p>
                <Button className="mt-4 h-10 text-sm" onClick={openCreateSupplier}>
                  <Plus className="h-4 w-4 mr-1" /> Add supplier
                </Button>
              </div>
            ) : (
              suppliers.map((s) => (
                <div
                  key={s.id}
                  className="rounded-lg border bg-card px-4 py-3.5 shadow-sm transition-colors hover:bg-muted/25"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-base font-semibold text-foreground">{s.name}</p>
                    <div className="flex items-center">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={() => openEditSupplier(s)}
                        title="Edit supplier"
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                        <span className="sr-only">Edit {s.name}</span>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 text-destructive hover:text-destructive"
                        onClick={() => setDeleteSupplierTarget(s)}
                        title="Delete supplier"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                        <span className="sr-only">Delete {s.name}</span>
                      </Button>
                    </div>
                  </div>
                  {s.commission_percent != null && s.commission_percent > 0 && (
                    <span className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {s.commission_percent}% commission · vendor gets {100 - s.commission_percent}%
                    </span>
                  )}
                  <div className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                    {s.contact_name && (
                      <p className="flex items-center gap-2">
                        <User className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        <span>{s.contact_name}</span>
                      </p>
                    )}
                    {s.phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        <span>{s.phone}</span>
                      </p>
                    )}
                    {s.email && (
                      <p className="flex items-center gap-2 break-all">
                        <Mail className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                        <span>{s.email}</span>
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm min-w-0">
          <CardHeader className="space-y-2 pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1 min-w-0">
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5 text-primary shrink-0" /> Purchase orders
                </CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Draft and submit orders, then record receipts when stock arrives. Click a column heading to sort;
                  newest orders appear first by default.
                </CardDescription>
              </div>
              <Button
                size="sm"
                className="h-10 shrink-0 text-sm sm:self-start"
                disabled={suppliers.length === 0}
                title={suppliers.length === 0 ? 'Add a supplier first' : undefined}
                onClick={openNewOrder}
              >
                <Plus className="h-4 w-4 mr-1" /> New PO
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-0 sm:px-6">
            {orders.length === 0 ? (
              <div className="mx-6 rounded-lg border border-dashed bg-muted/20 p-6 text-center sm:mx-0">
                <p className="text-base font-medium text-foreground">No purchase orders yet</p>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  {suppliers.length === 0
                    ? 'Add at least one supplier, then use New PO to create your first order.'
                    : 'Create a draft purchase order, add lines, then submit when you place the order with the vendor.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border-t sm:rounded-md sm:border sm:mx-0">
                <table className="w-full min-w-[720px] text-left text-base">
                  <thead className="bg-muted/50 text-sm">
                    <tr className="border-b">
                      <th className="px-4 py-3 font-semibold">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 font-semibold text-foreground hover:text-primary"
                          onClick={() => onPoSort('supplier_name')}
                        >
                          Supplier
                          {poSortIcon('supplier_name')}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold whitespace-nowrap">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 font-semibold text-foreground hover:text-primary"
                          onClick={() => onPoSort('created_at')}
                        >
                          Created
                          {poSortIcon('created_at')}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold whitespace-nowrap">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 font-semibold text-foreground hover:text-primary"
                          onClick={() => onPoSort('expected_date')}
                        >
                          Expected
                          {poSortIcon('expected_date')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                        <button
                          type="button"
                          className="inline-flex w-full items-center justify-end gap-1.5 font-semibold text-foreground hover:text-primary"
                          onClick={() => onPoSort('total_ordered_qty')}
                        >
                          Qty (lines)
                          {poSortIcon('total_ordered_qty')}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold whitespace-nowrap">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 font-semibold text-foreground hover:text-primary"
                          onClick={() => onPoSort('status')}
                        >
                          Status
                          {poSortIcon('status')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold min-w-[200px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sortedOrders.map((o) => (
                      <tr key={o.id} className="bg-card hover:bg-muted/20">
                        <td className="px-4 py-3.5 align-middle font-medium text-foreground">{o.supplier_name}</td>
                        <td className="px-4 py-3.5 align-middle text-muted-foreground tabular-nums whitespace-nowrap">
                          {formatDateHuman(o.created_at)}
                        </td>
                        <td className="px-4 py-3.5 align-middle text-muted-foreground tabular-nums whitespace-nowrap">
                          {o.expected_date ? formatDateHuman(o.expected_date) : '—'}
                        </td>
                        <td className="px-4 py-3.5 align-middle text-right tabular-nums font-medium">
                          {o.total_ordered_qty}
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <Badge variant={poStatusBadgeVariant(o.status)} className="text-xs font-semibold">
                            {formatPoStatusLabel(o.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <div className="flex flex-wrap items-center justify-center gap-2">
                            <>
                                {(o.status === 'draft' || o.status === 'pending_approval' || o.status === 'approved') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-9 text-sm gap-1.5"
                                    disabled={printingPoId === o.id}
                                    onClick={async () => {
                                      setPrintingPoId(o.id)
                                      try {
                                        const r = await apiClient.getPurchaseOrder(o.id)
                                        if (r.success && r.data) {
                                          setPoPrintDetail(r.data)
                                          setPoPrintOpen(true)
                                        } else {
                                          showToast('error', r.message || 'Could not load purchase order')
                                        }
                                      } catch (err) {
                                        showToast('error', err instanceof Error ? err.message : 'Could not load purchase order')
                                      } finally {
                                        setPrintingPoId(null)
                                      }
                                    }}
                                  >
                                    <Printer className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                    Print
                                  </Button>
                                )}
                                {o.status === 'pending_approval' && (
                                  <Button size="sm" variant="secondary" className="h-9 text-sm" disabled={approvePO.isPending} onClick={() => approvePO.mutate(o.id)}>
                                    Approve
                                  </Button>
                                )}
                                {o.status === 'approved' && (
                                  <Button size="sm" variant="secondary" className="h-9 text-sm" disabled={submitPO.isPending} onClick={() => submitPO.mutate(o.id)}>
                                    Submit
                                  </Button>
                                )}
                                {(o.status === 'ordered' || o.status === 'partially_received') && (
                                  <Button size="sm" variant="outline" className="h-9 text-sm" onClick={() => openReceive(o.id)}>
                                    Receive…
                                  </Button>
                                )}
                                {o.status === 'partially_received' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-9 text-sm"
                                    disabled={closePO.isPending}
                                    title="Close this PO without receiving the remaining quantity"
                                    onClick={() => { setCloseTarget(o); setCloseReason('') }}
                                  >
                                    Close
                                  </Button>
                                )}
                                {(o.status === 'partially_received' || o.status === 'received') && (
                                  <Button size="sm" variant="outline" className="h-9 text-sm gap-1.5" onClick={() => openReturn(o.id)}>
                                    <RotateCcw className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                    Return
                                  </Button>
                                )}
                                {o.status !== 'cancelled' && (
                                  <Button size="sm" variant="ghost" className="h-9 text-sm" disabled={sendWhatsApp.isPending} onClick={() => sendWhatsApp.mutate(o.id)} title="Send to supplier via WhatsApp">
                                    WhatsApp
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="h-9 text-sm gap-1.5"
                                  disabled={deletePO.isPending || o.status === 'partially_received' || o.status === 'received'}
                                  title={
                                    o.status === 'partially_received' || o.status === 'received'
                                      ? 'Purchase orders with received stock cannot be deleted'
                                      : undefined
                                  }
                                  onClick={() => setDeleteTarget(o)}
                                >
                                  <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                  Delete
                                </Button>
                            </>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={supplierModal} onOpenChange={(open) => { if (!open) { setSupplierModal(false); setEditingSupplier(null) } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">{editingSupplier ? 'Edit supplier' : 'New supplier'}</DialogTitle>
            <DialogDescription className="text-base">
              Add a vendor you can link to purchase orders and receipts. Set a commission
              % to run vendor-wise sales/payout reports.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label htmlFor="sup-name" className="text-base">Name</Label>
              <Input id="sup-name" className="h-11 text-base" value={supplierForm.name} onChange={(e) => setSupplierForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sup-contact" className="text-base">Contact</Label>
              <Input id="sup-contact" className="h-11 text-base" value={supplierForm.contact_name} onChange={(e) => setSupplierForm((f) => ({ ...f, contact_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sup-phone" className="text-base">Phone</Label>
              <Input id="sup-phone" className="h-11 text-base" value={supplierForm.phone} onChange={(e) => setSupplierForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sup-email" className="text-base">Email</Label>
              <Input id="sup-email" type="email" className="h-11 text-base" value={supplierForm.email} onChange={(e) => setSupplierForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sup-commission" className="text-base">Commission % (house cut)</Label>
              <Input
                id="sup-commission"
                type="number"
                min={0}
                max={100}
                step="0.01"
                placeholder="e.g. 25"
                className="h-11 text-base"
                value={supplierForm.commission_percent}
                onChange={(e) => setSupplierForm((f) => ({ ...f, commission_percent: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Your cut on this vendor's sales. Vendor is paid the rest
                {supplierForm.commission_percent.trim() !== '' && Number.isFinite(parseFloat(supplierForm.commission_percent))
                  ? ` (${(100 - parseFloat(supplierForm.commission_percent)).toFixed(2).replace(/\.00$/, '')}%)`
                  : ''}
                . Leave 0 for regular (non-consignment) suppliers.
              </p>
            </div>
            <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={supplierForm.is_consignment}
                onChange={(e) => setSupplierForm((f) => ({ ...f, is_consignment: e.target.checked }))}
              />
              <span className="text-sm">
                <span className="font-medium">Consignment vendor</span>
                <span className="block text-xs text-muted-foreground">
                  Their stock isn't purchased — receiving a PO won't create a cost bill.
                  You settle them from sales (the payout) on the Vendor Reports tab.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="text-base h-11" onClick={() => { setSupplierModal(false); setEditingSupplier(null) }}>
              Cancel
            </Button>
            <Button
              className="text-base h-11"
              disabled={!supplierForm.name.trim() || createSup.isPending || updateSup.isPending}
              onClick={() => (editingSupplier ? updateSup.mutate() : createSup.mutate())}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteSupplierTarget}
        onOpenChange={(open) => {
          if (!open && !deleteSupplier.isPending) setDeleteSupplierTarget(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Delete supplier</DialogTitle>
            <DialogDescription className="text-base">
              {deleteSupplierTarget
                ? `Delete "${deleteSupplierTarget.name}"? Suppliers already linked to purchase orders cannot be deleted.`
                : 'Delete this supplier?'}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
            If this supplier has purchase history, the server will block deletion so your purchasing records stay intact.
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="text-base h-11" onClick={() => setDeleteSupplierTarget(null)} disabled={deleteSupplier.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="text-base h-11"
              disabled={!deleteSupplierTarget || deleteSupplier.isPending}
              onClick={() => deleteSupplier.mutate()}
            >
              {deleteSupplier.isPending ? 'Deleting…' : 'Delete supplier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={poModal} onOpenChange={setPoModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">New purchase order</DialogTitle>
            <DialogDescription className="text-base">
              Choose a supplier and lines. A purchase order needs admin approval before it can be issued.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-1 text-base">
            <div className="space-y-2">
              <Label htmlFor="po-supplier" className="text-base">Supplier</Label>
              <select
                id="po-supplier"
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={poForm.supplier_id}
                onChange={(e) => setPoForm((f) => ({ ...f, supplier_id: e.target.value }))}
              >
                <option value="">Select supplier…</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="po-expected" className="text-base">Expected date</Label>
              <DatePicker
                id="po-expected"
                className="h-11 text-base"
                value={poForm.expected_date}
                onChange={(v) => setPoForm((f) => ({ ...f, expected_date: v }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="po-notes" className="text-base">Notes</Label>
              <Textarea id="po-notes" className="min-h-[88px] text-base resize-y" value={poForm.notes} onChange={(e) => setPoForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional notes for this PO…" />
            </div>
            <div className="space-y-2">
              <Label className="text-base font-semibold">Lines</Label>
              <div className="rounded-md border overflow-hidden">
                <table className="w-full text-base">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left font-semibold text-sm px-3 py-2.5">Item</th>
                      <th className="text-right font-semibold text-sm px-3 py-2.5 w-[170px]">Qty</th>
                      <th className="text-right font-semibold text-sm px-3 py-2.5 w-[130px]">Unit cost</th>
                      <th className="w-[100px] px-2 py-2.5" aria-label="Row actions" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {poForm.lines.map((ln, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 align-middle">
                          <select
                            className="h-11 w-full rounded-md border border-input bg-background px-2 text-base"
                            value={ln.stock_item_id}
                            onChange={(e) => {
                              const next = [...poForm.lines]
                              next[idx] = { ...next[idx], stock_item_id: e.target.value }
                              setPoForm((f) => ({ ...f, lines: next }))
                            }}
                          >
                            <option value="">Select item…</option>
                            {stockItems.map((it) => (
                              <option key={it.id} value={it.id}>
                                {it.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="number"
                              className="h-11 text-base text-right tabular-nums flex-1 min-w-0"
                              placeholder="Qty"
                              value={ln.quantity_ordered || ''}
                              onChange={(e) => {
                                const next = [...poForm.lines]
                                next[idx] = { ...next[idx], quantity_ordered: parseFloat(e.target.value) || 0 }
                                setPoForm((f) => ({ ...f, lines: next }))
                              }}
                            />
                            <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                              {stockItems.find((s) => s.id === ln.stock_item_id)?.unit ?? ''}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Input
                            type="number"
                            step="0.01"
                            className="h-11 text-base text-right tabular-nums"
                            placeholder="Optional"
                            value={ln.unit_cost ?? ''}
                            onChange={(e) => {
                              const next = [...poForm.lines]
                              const v = e.target.value
                              next[idx] = {
                                ...next[idx],
                                unit_cost: v === '' ? undefined : parseFloat(v),
                              }
                              setPoForm((f) => ({ ...f, lines: next }))
                            }}
                          />
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <div className="flex gap-1 justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-11 w-11 shrink-0"
                              aria-label="Add line"
                              onClick={() =>
                                setPoForm((f) => ({
                                  ...f,
                                  lines: [...f.lines, { stock_item_id: '', quantity_ordered: 1, unit_cost: undefined }],
                                }))
                              }
                            >
                              +
                            </Button>
                            {poForm.lines.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-11 shrink-0"
                                aria-label="Remove line"
                                onClick={() => setPoForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))}
                              >
                                −
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="text-base h-11" onClick={() => setPoModal(false)}>
              Cancel
            </Button>
            <Button
              className="text-base h-11"
              disabled={!poForm.supplier_id || poForm.lines.every((l) => !l.stock_item_id) || createPO.isPending}
              onClick={() => createPO.mutate()}
            >
              Create draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PoPrintModal
        open={poPrintOpen}
        onOpenChange={(o) => {
          setPoPrintOpen(o)
          if (!o) setPoPrintDetail(null)
        }}
        detail={poPrintDetail}
        businessName={poPrintBusinessName}
        formatCurrency={formatCurrency}
        receiptSettings={poReceiptSettings}
      />

      <Dialog open={!!receivePoId} onOpenChange={(o) => !o && setReceivePoId(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Receive goods</DialogTitle>
            <DialogDescription className="text-base">
              Enter quantities received. Optional expiry and unit cost override batch defaults.
            </DialogDescription>
          </DialogHeader>
          {!detail ? (
            <p className="text-base text-muted-foreground py-8">Loading…</p>
          ) : (
            <div className="space-y-4 text-base">
              <p className="text-muted-foreground">
                {detail.supplier_name} · {detail.status}
              </p>
              {detail.lines.map((ln) => {
                const open = ln.quantity_ordered - ln.quantity_received
                const r = receiveLines[ln.id] ?? { qty: 0, expiry: '', unit_cost: ln.unit_cost != null ? String(ln.unit_cost) : '', reason: '' }
                const isVariance = r.qty > 0 && Math.abs(open - r.qty) > 0.0001
                return (
                  <div key={ln.id} className="border rounded-lg p-4 space-y-3">
                    <div className="font-semibold text-base">
                      {ln.item_name}{' '}
                      <span className="text-muted-foreground text-sm font-normal">({ln.unit})</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Open to receive: {open.toFixed(2)}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-base">Qty</Label>
                        <Input
                          type="number"
                          className="h-11 text-base tabular-nums"
                          value={r.qty || ''}
                          onChange={(e) =>
                            setReceiveLines((m) => ({
                              ...m,
                              [ln.id]: { ...r, qty: parseFloat(e.target.value) || 0 },
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-base">Expiry</Label>
                        <DatePicker
                          className="h-11 text-base"
                          value={r.expiry}
                          onChange={(v) =>
                            setReceiveLines((m) => ({
                              ...m,
                              [ln.id]: { ...r, expiry: v },
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-base">Unit cost</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-11 text-base tabular-nums"
                          value={r.unit_cost}
                          onChange={(e) =>
                            setReceiveLines((m) => ({
                              ...m,
                              [ln.id]: { ...r, unit_cost: e.target.value },
                            }))
                          }
                        />
                      </div>
                    </div>
                    {isVariance && (
                      <div className="space-y-2">
                        <Label className="text-base text-amber-700">
                          Variance reason — received {r.qty.toFixed(2)} vs {open.toFixed(2)} ordered (required)
                        </Label>
                        <Input
                          className="h-11 text-base"
                          placeholder="e.g. short delivery, damaged units rejected…"
                          value={r.reason}
                          onChange={(e) =>
                            setReceiveLines((m) => ({
                              ...m,
                              [ln.id]: { ...r, reason: e.target.value },
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {detail && (
            <label className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={receiveFinalize}
                onChange={(e) => setReceiveFinalize(e.target.checked)}
              />
              <span>
                <span className="font-medium">Finalize &amp; close this PO after recording</span> — closes the order even if some
                ordered quantity is still open (e.g. the vendor short-shipped). The received stock and your variance reasons are
                kept in Movements; the PO leaves the active list. Leave unchecked to keep the remainder open for another delivery.
              </span>
            </label>
          )}
          {detail && (
            <label className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={receivePostToExpense}
                onChange={(e) => setReceivePostToExpense(e.target.checked)}
              />
              <span>
                <span className="font-medium">Add this purchase to expenses now</span> — records the received value as an
                expense today (cash-basis). Leave unchecked to treat the stock as an asset that becomes an expense (COGS) as
                it&apos;s sold, which avoids counting the same cost twice.
              </span>
            </label>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="text-base h-11" onClick={() => setReceivePoId(null)}>
              Cancel
            </Button>
            <Button
              className="text-base h-11"
              disabled={!detail || receivePO.isPending}
              onClick={() => {
                if (!detail || !receivePoId) return
                const lines = buildReceivePayload(detail)
                if (lines.length === 0) {
                  showToast('error', 'Enter at least one line quantity to receive')
                  return
                }
                // Verifiable receiving: any short/over receipt needs a reason.
                const missingReason = detail.lines.some((ln) => {
                  const r = receiveLines[ln.id]
                  if (!r || r.qty <= 0) return false
                  const open = ln.quantity_ordered - ln.quantity_received
                  return Math.abs(open - r.qty) > 0.0001 && !(r.reason && r.reason.trim())
                })
                if (missingReason) {
                  showToast('error', 'A variance reason is required when received qty differs from ordered')
                  return
                }
                receivePO.mutate({ id: receivePoId, lines, finalize: receiveFinalize })
              }}
            >
              {receiveFinalize ? 'Record & close PO' : 'Record receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closeTarget} onOpenChange={(open) => !open && setCloseTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Close purchase order</DialogTitle>
            <DialogDescription className="text-base">
              Close this PO and stop expecting the remaining quantity. The stock you already received and any variance
              reasons stay in Movements; the PO leaves the active list. Raise a new PO when you reorder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="po-close-reason" className="text-base">Reason (optional)</Label>
            <Textarea
              id="po-close-reason"
              className="min-h-[88px] text-base resize-y"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="e.g. vendor short-shipped; remainder won't arrive"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="text-base h-11" onClick={() => setCloseTarget(null)}>
              Cancel
            </Button>
            <Button
              className="text-base h-11"
              disabled={closePO.isPending}
              onClick={() => { if (closeTarget) closePO.mutate({ id: closeTarget.id, reason: closeReason.trim() }) }}
            >
              {closePO.isPending ? 'Closing…' : 'Close PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Delete purchase order</DialogTitle>
            <DialogDescription className="text-base">
              This permanently removes an unreceived purchase order. Enter an admin or manager PIN and a reason to
              continue.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4 py-1">
              <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                <p className="font-semibold text-foreground">{deleteTarget.supplier_name}</p>
                <p className="mt-1 text-muted-foreground">
                  {formatPoStatusLabel(deleteTarget.status)} · Created {formatDateHuman(deleteTarget.created_at)}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="po-delete-reason" className="text-base">Reason</Label>
                <Textarea
                  id="po-delete-reason"
                  className="min-h-24 text-base"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Example: Duplicate PO created by mistake"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="po-delete-pin" className="text-base">Admin / manager PIN</Label>
                <Input
                  id="po-delete-pin"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  className="h-11 text-base tracking-[0.4em]"
                  value={deletePin}
                  onChange={(e) => setDeletePin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="text-base h-11" onClick={closeDeleteDialog} disabled={deletePO.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="text-base h-11"
              disabled={!deleteTarget || deletePin.length !== 4 || !deleteReason.trim() || deletePO.isPending}
              onClick={() => deletePO.mutate()}
            >
              {deletePO.isPending ? 'Deleting…' : 'Delete PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!returnPoId} onOpenChange={(open) => !open && closeReturnDialog()}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Return stock to supplier</DialogTitle>
            <DialogDescription className="text-base">
              Record stock sent back to the supplier. This creates a new return movement and keeps the original PO receipt
              history intact.
            </DialogDescription>
          </DialogHeader>
          {!returnDetail ? (
            <p className="text-base text-muted-foreground py-8">Loading…</p>
          ) : returnableLines.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-muted/20 p-5 text-sm text-muted-foreground">
              No returnable stock remains for this purchase order. It may already have been used, adjusted, or returned.
            </div>
          ) : (
            <div className="space-y-4 py-1">
              <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                <p className="font-semibold text-foreground">{returnDetail.supplier_name}</p>
                <p className="mt-1 text-muted-foreground">
                  Choose the received line you are returning. Expiring batches are deducted first.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="po-return-line" className="text-base">Returned item</Label>
                  <select
                    id="po-return-line"
                    className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={returnLineId}
                    onChange={(e) => {
                      setReturnLineId(e.target.value)
                      setReturnQty('')
                    }}
                  >
                    <option value="">Select received line…</option>
                    {returnableLines.map((ln) => (
                      <option key={ln.id} value={ln.id}>
                        {ln.item_name} · returnable {(ln.quantity_returnable ?? ln.quantity_received).toFixed(2)} {ln.unit}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="po-return-qty" className="text-base">Quantity</Label>
                  <Input
                    id="po-return-qty"
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-11 text-base tabular-nums"
                    value={returnQty}
                    onChange={(e) => setReturnQty(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="po-return-refund-type" className="text-base">Refund type</Label>
                  <select
                    id="po-return-refund-type"
                    className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={returnRefundType}
                    onChange={(e) => {
                      const next = e.target.value as typeof returnRefundType
                      setReturnRefundType(next)
                      if (next === 'no_refund') setReturnRefundAmount('')
                    }}
                  >
                    <option value="supplier_credit">Supplier credit</option>
                    <option value="cash">Cash refund</option>
                    <option value="bank">Bank refund</option>
                    <option value="no_refund">No refund</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="po-return-refund" className="text-base">Refund amount</Label>
                  <Input
                    id="po-return-refund"
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-11 text-base tabular-nums"
                    value={returnRefundAmount}
                    disabled={returnRefundType === 'no_refund'}
                    onChange={(e) => setReturnRefundAmount(e.target.value)}
                    placeholder={returnRefundType === 'no_refund' ? 'Not applicable' : '0.00'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="po-return-pin" className="text-base">Admin / manager PIN</Label>
                  <Input
                    id="po-return-pin"
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    className="h-11 text-base tracking-[0.4em]"
                    value={returnPin}
                    onChange={(e) => setReturnPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="po-return-reason" className="text-base">Reason</Label>
                  <Textarea
                    id="po-return-reason"
                    className="min-h-24 text-base"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    placeholder="Example: Expired before use; supplier agreed to credit"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="text-base h-11" onClick={closeReturnDialog} disabled={returnPO.isPending}>
              Cancel
            </Button>
            <Button
              className="text-base h-11"
              disabled={
                !returnLineId ||
                !(Number(returnQty) > 0) ||
                !returnReason.trim() ||
                returnPin.length !== 4 ||
                returnPO.isPending ||
                (returnRefundType !== 'no_refund' && Number(returnRefundAmount || 0) < 0)
              }
              onClick={() => returnPO.mutate()}
            >
              {returnPO.isPending ? 'Recording…' : 'Record return'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
